/** @jsxImportSource @emotion/react */
import PropTypes from 'prop-types';
import { css } from '@emotion/react';
import React, { useEffect, useState } from 'react';

import Text from 'components/Text';
import { MEDIA_QUERIES } from 'styles/constants';
import NewSales from 'assets/images/NewSales.png';
import NewClients from 'assets/images/NewClients.png';
import NewFollowers from 'assets/images/NewFollowers.png';
import FriendsDinner from 'assets/images/FriendsDinner.png';
import { BLUE_FEEK, PURPLE_FEEK, PURPLE_LIGHT } from 'styles/colors';
import OrganicAdvertising from 'assets/images/OrganicAdvertising.png';

const styles = css`
  display: flex;
  padding-top: 100px;
  flex-direction: column;

  .container {
    display: flex;
    padding-left: 18px;
    justify-content: flex-start;
    align-items: center;
    gap: 50px;
    overflow: hidden;
    margin-left: -15vw;
  }

  h1,
  h2,
  h3 {
    margin: 0;
  }

  .card {
    background-color: ${PURPLE_LIGHT};
    border-radius: 20px;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
  .badge-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 14px;
    width: fit-content;
    align-self: center;
    padding: 8px;
    border-radius: 30px;
    margin-bottom: 40px;
    background-color: ${PURPLE_LIGHT};
  }
  .badge-number {
    height: 34px;
    display: flex;
    min-width: 34px;
    padding: 0 10px;
    border-radius: 30px;
    align-items: center;
    cursor: pointer;
    justify-content: center;
    background-color: ${BLUE_FEEK}20;
  }
  .badge-number.active {
    background-color: ${PURPLE_FEEK};
    color: white;
  }

  .badge-number-card {
    border-radius: 50%;
    border: 1px solid ${PURPLE_FEEK};
    height: 34px;
    width: 34px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
  }

  .card-body {
    width: 25vw;
    flex: 1;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .previous-card,
  .next-card {
    opacity: 0.4;
  }

  ${MEDIA_QUERIES.smallScreen} {
    .container {
      margin-left: -65vw;
    }
    .card-body {
      width: 40vw;
    }
  }
  ${MEDIA_QUERIES.tablet} {
    .previous-card,
    .next-card {
      display: none;
    }
    .container {
      margin-left: 0px;
      padding-left: 0px;
    }
    .card {
      border-radius: 0;
      flex-direction: column;
    }
    .card-body {
      width: fit-content;
    }
    .card img {
      width: 80%;
      margin-bottom: 18px;
    }
  }
`;

const cards = [
  {
    number: 1,
    title: 'Que ofrecemos.',
    subtitle: '¿Por qué Feek?',
    description: (
      <>
        <b>Feek</b> es la <u>herramienta ideal</u> para hacer crecer
        tu negocio de manera orgánica y genuina, ayudando a
        posicionarlo en nuevos segmentos de mercado.
      </>
    ),
    image: FriendsDinner,
    textButtonActive: 'Feek',
  },
  {
    number: 2,
    title: 'Atraemos',
    subtitle: 'Nuevos Clientes',
    description: (
      <>
        La exposición a través de nuestros usuarios posiciona a tu
        negocio en distintos sectores llenos de nuevos clientes
        potenciales.
      </>
    ),
    image: NewClients,
    textButtonActive: 'Clientes',
  },
  {
    number: 3,
    title: 'Convertimos a',
    subtitle: 'Ventas Generadas',
    description: (
      <>
        Nuestro sistema <u>atrae usuarios</u> directamente a tu
        negocio; los clientes aprovechan las promociones y generan
        consumo.
      </>
    ),
    image: NewSales,
    textButtonActive: 'Ventas',
  },
  {
    number: 4,
    title: 'Convertimos a',
    subtitle: 'Nuevos Seguidores',
    description: (
      <>
        Los seguidores de nuestros usuarios son ahora tus seguidores;
        ven de primera mano lo que tu negocio ofrece, se enamoran y
        comienzan a seguirlo.
      </>
    ),
    image: NewFollowers,
    textButtonActive: 'Seguidores',
  },
  {
    number: 5,
    title: 'Convertimos a',
    subtitle: 'Publicidad Orgánica',
    description: (
      <>
        Nuestros usuarios comparten en redes su experiencia en tu
        negocio; garantizando <u>impresiones orgánicas y genuinas</u>{' '}
        entre su círculo social.
      </>
    ),
    image: OrganicAdvertising,
    textButtonActive: 'Publicidad',
  },
];

const WhyFeek = () => {
  const [activeCard, setActiveCard] = useState(0);
  const [card, setCard] = useState(cards[0]);
  const [previousCard, setPreviousCard] = useState(
    cards[cards.length - 1],
  );
  const [nextCard, setNextCard] = useState(cards[1]);

  useEffect(() => {
    setCard(cards[activeCard]);
    setPreviousCard(
      cards[activeCard - 1 < 0 ? cards.length - 1 : activeCard - 1],
    );
    setNextCard(
      cards[activeCard + 1 >= cards.length ? 0 : activeCard + 1],
    );
  }, [activeCard]);

  return (
    <div css={styles} id="benefits">
      <div className="badge-container">
        {cards.map((card, index) => (
          <Text
            key={card.number}
            className={`badge-number ${
              activeCard === index ? 'active' : ''
            }`}
            color={PURPLE_FEEK}
            fontSize={16}
            fontWeight={600}
            onClick={() => setActiveCard(index)}
          >
            {activeCard === index
              ? card.textButtonActive
              : card.number}
          </Text>
        ))}
      </div>
      <div className="container">
        <>
          <Card card={previousCard} className="previous-card" />
          <Card card={card} />
          <Card card={nextCard} className="next-card" />
        </>
      </div>
    </div>
  );
};

const Card = ({ card, className }) => {
  return (
    <div className={`card ${className}`}>
      <div className="card-body">
        <Text
          className="badge-number-card"
          color={PURPLE_FEEK}
          fontSize={16}
          fontWeight={600}
        >
          {card.number}
        </Text>
        <div>
          <Text color={PURPLE_FEEK} fontSize={14}>
            {card.title}
          </Text>
          <Text
            type="h3"
            color={PURPLE_FEEK}
            fontWeight={600}
            fontSize={24}
          >
            {card.subtitle}
          </Text>
        </div>
        <Text fontSize={16}>{card.description}</Text>
      </div>
      <img src={card.image} alt={`Feek - ${card.subtitle}`} />
    </div>
  );
};

Card.propTypes = {
  card: PropTypes.shape({
    number: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string.isRequired,
    description: PropTypes.node.isRequired,
    image: PropTypes.string.isRequired,
    textButtonActive: PropTypes.string.isRequired,
  }).isRequired,
  className: PropTypes.string,
};

export default WhyFeek;
